






















































































































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";

// Enums
import { DataModules } from "@/store/modules/data";

// Types
import { IClient, IClientLoginForm } from "@/models/clients";
import { IState } from "@/store";

@Component({
  components: {
    "two-fa-switch": () =>
      import("@/components/app/global/auth/TwoFaSwitch.vue")
  },
  computed: {
    ...mapState({
      clientScope(state: IState, getters) {
        return getters[`data/${DataModules.CLIENTS}/scope`](this.clientId);
      },
      client(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CLIENTS,
          scope: this.clientScope
        });
      }
    })
  }
})
export default class ClientLogin extends Mixins(
  RequestPromises,
  HasValidation,
  Context
) {
  // Map state

  public client!: IClient;
  public clientScope!: string;

  // Props

  @Prop({ required: true })
  public clientId!: IClient["id"];

  @Prop({ default: true })
  public isCollapse!: boolean;

  // Data

  public form = {} as IClientLoginForm;
  public initialForm = {} as IClientLoginForm;
  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;
  public isProcessing = false;
  public isSendingResetPassword = false;

  // Lif-cycle methods

  public created() {
    if (!this.contentIsAvailable) return;

    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CLIENTS,
      scope: this.clientScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await this.getClientData(this.isReloading);
      this.initiateClientForm();
      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isReloading = false;
      this.isLoading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getClientData(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS}/getClientWithRelatedData`,
      {
        id: this.clientId,
        scope: this.clientScope,
        ignoreStored,
        vm: this
      }
    );
  }

  public getUserData() {
    if (!this.isAdmin) return this.$store.dispatch("auth/client/getUser");
    return Promise.resolve();
  }

  public initiateClientForm() {
    this.form = _.pick(this.client, ["username", "has_login"]);
    this.initialForm = _.clone(this.form);
  }

  public async confirmPassword() {
    const confirmPassword = await this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () => import("@/components/base/ConfirmPasswordModal.vue"),
        width: 480,
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.confirm"),
          message: this.$t("_sentence.change_login_details_confirmation"),
          action: this.$t("_action.confirm")
        },
        canCancel: ["button"],
        events: {
          confirmed: async (currentPassword: string) => {
            if (currentPassword) {
              await this.updateClientLoginDetails({
                ...this.form,
                password: currentPassword
              });
            }
            confirmPassword.close();
          },
          close: () => {
            this.form.username = this.client.username;
            this.isProcessing = false;
          }
        }
      }
    });
  }

  public async validateBeforeSubmit() {
    if (!this.isChanged) return;
    this.isAdmin ? this.updateClientLoginDetails() : this.confirmPassword();
  }

  public async sendResetInstruction() {
    try {
      this.isSendingResetPassword = true;

      await this.resetPassword();

      this.$store.dispatch("toast/show", {
        message: this.$t("_.reset_password_email_sent")
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isSendingResetPassword = false;
    }
  }

  public openSetPasswordModal() {
    this.$store.dispatch(`data/${DataModules.CLIENTS}/openSetPasswordModal`, {
      props: {
        clientId: this.client.id
      }
    });
  }

  public resetPassword() {
    return this.$store.dispatch(`data/${DataModules.CLIENTS}/resetPassword`, {
      id: this.clientId
    });
  }

  public async updateClientLoginDetails(data: IClientLoginForm | null = null) {
    this.isProcessing = true;

    try {
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      await this.updateClientLogin(data || this.form);
      await Promise.all([this.getClientData(true), this.getUserData()]);

      this.initiateClientForm();

      this.$store.dispatch("toast/show", {
        message: this.$t("_sentence.login_details_successfully_updated")
      });
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public updateClientLogin(data) {
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS}/updateLoginDetails`,
      {
        id: this.clientId,
        data
      }
    );
  }

  // Computed

  public get defaultEmailAddress() {
    return this.client?.default_email?.email;
  }

  public get isChanged(): boolean {
    return !_.isEqual(this.form, this.initialForm);
  }

  // Guarding

  public get canUpdate() {
    return this.$userCan("update_client");
  }

  public get guardFunctionalities() {
    return ["get_client"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Watchers

  @Watch("isChanged")
  public onFormChange() {
    this.$emit("formChanged", this.isChanged);
  }

  @Watch("isProcessing")
  public onIsProcessingChange() {
    this.$emit("isProcessingChanged", this.isProcessing);
  }

  @Watch("hasErrors")
  public onHasErrorsChange() {
    this.$emit("hasErrorsChanged", this.hasErrors);
  }
}
